<template>
  <modal
    header
    :footer="false"
    disable-click-outside
    class="google-connect-modal"
    data-test="graphics-sportsradarConnectModal"
    :wide="!!statType"
    :tall="!!statType"
    @close="onCancel"
  >
    <!-- Header -->
    <template #modal-header>
      <p
        v-if="overline"
        class="google-connect-modal__overline"
      >
        {{ overline }}
      </p>
      <h3>{{ modalTitle }}</h3>
      <p
        v-if="showNoLeagueText"
        class="text-danger text-tiny"
      >
        Oh no! It appears you don't have access to this. Please reach out and we can get you set up.
      </p>
      <button
        class="modal-close"
        data-test="graphics-sportsradarConnectModal-close"
        @click="onCancel"
      >
        <span class="fa fa-times"></span>
      </button>
    </template>

    <!-- Body -->
    <template #modal-body>
      <transition-group
        tag="div"
        class="full-height"
        :name="isFromTemplate ? '' : transitionName"
      >
        <div
          v-show="isLoadingData"
          key="loader"
          class="loader"
          data-test="graphics-sportsradarConnectModal-loader"
        >
          <span class="fa-solid fa-spinner fa-spin" />
        </div>

        <div
          v-show="!isLoadingData"
          :key="isFromTemplate ? 'stepper' : `stepper-${statType ? 'stats' : 'type'}`"
          class="full-height"
          data-test="graphics-sportsradarConnectModal-stepper"
        >
          <!-- Select Type -->
          <div
            v-if="!statType"
            class="form google-connect-form"
            data-test="graphics-sportsradarConnectModal-step1-picker"
          >
            <div
              class="sportradar-type-picker__section"
              data-test="graphics-in-game-statistics-section"
            >
              <p class="sportradar-type-picker__section-title">
                In-Game Statistics
              </p>
              <p class="sportradar-type-picker__section-subtitle">
                Feature stats from a specific game
              </p>
              <div class="sportradar-type-picker">
                <button
                  class="sportradar-type-picker__button"
                  data-test="graphics-sportsradarConnectModal-step1-gameStatsPlayer"
                  data-entity="graphics-sportsradarConnectModal-step1-gameStatsPlayer"
                  @click="setType('player', 'game')"
                >
                  Player Profile
                </button>
                <span class="fas fa-arrow-right"></span>
              </div>

              <div class="sportradar-type-picker">
                <button
                  class="sportradar-type-picker__button"
                  data-test="graphics-sportsradarConnectModal-step1-gameStatsTeam"
                  data-entity="graphics-sportsradarConnectModal-step1-gameStatsTeam"
                  @click="setType('team', 'game')"
                >
                  Team Profile
                </button>
                <span class="fas fa-arrow-right"></span>
              </div>
            </div>

            <div
              class="sportradar-type-picker__section"
              data-test="graphics-season-statistics-section"
            >
              <p class="sportradar-type-picker__section-title">
                Season Statistics
              </p>
              <p class="sportradar-type-picker__section-subtitle">
                Feature stats from a season
              </p>
              <div class="sportradar-type-picker">
                <button
                  class="sportradar-type-picker__button"
                  data-test="graphics-sportsradarConnectModal-step1-seasonStatsPlayer"
                  data-entity="graphics-sportsradarConnectModal-step1-seasonStatsPlayer"
                  @click="setType('player', 'season')"
                >
                  Player Profile
                </button>
                <span class="fas fa-arrow-right"></span>
              </div>

              <div class="sportradar-type-picker">
                <button
                  class="sportradar-type-picker__button"
                  data-test="graphics-sportsradarConnectModal-step1-seasonStatsTeam"
                  data-entity="graphics-sportsradarConnectModal-step1-seasonStatsTeam"
                  @click="setType('team', 'season')"
                >
                  Team Profile
                </button>
                <span class="fas fa-arrow-right"></span>
              </div>
            </div>
          </div>

          <!-- Stat Selector -->
          <stat-selector
            v-else-if="sportradarElementId"
            :provider-id="sportradarElementId"
            :stat-type="statType"
            :stat-period="statPeriod"
            :allow-back="!isFromTemplate"
            :stat-limit="statLimit"
            @previous-step="goBack()"
            @next-step="createGraphic"
          />
        </div>
      </transition-group>
    </template>
  </modal>
</template>

<script>
import { mapActions, mapGetters } from 'vuex';

import Modal from '../../Modal/Modal.vue';
import StatSelector from './StatSelector.vue';

import leagueInfo from '../../../utils/leagueInfo';

const SlideTransitionName = 'modal-form__slide';

const accountUrl = process.env.TAGBOARD_ACCOUNT_URL;

export default {
  emits: ['close'],
  components: {
    Modal,
    StatSelector,
  },

  props: {
    leagueName: {
      type: String,
      default: null,
    },
  },

  data() {
    return {
      statType: '',
      statPeriod: '',
      modalTitle: 'Select Stat Type',
      overline: null,
      saving: false,
      isLoadingData: false,
      transitionName: SlideTransitionName,
      apiKey: '',
      isFromTemplate: false,
      sportradarElementId: null,
      templateParams: null,
      showNoLeagueText: false,
      accountUrl,
    };
  },
  computed: {
    ...mapGetters(['availableSportradarLeagues']),

    statLimit() {
      if (this.isFromTemplate) {
        switch (this.statType) {
          case 'team':
            return 7;
          default: // player
            return 4;
        }
      }

      return -1;
    },
  },
  async created() {
    // Pre-made template flow (query params drive everything)
    if (this.$route.name === 'create' && this.$route.query.provider) {
      this.isFromTemplate = true;
      this.modalTitle = '';
      this.isLoadingData = true;
      const { template, leagueName, statType } = this.$route.query;

      this.templateParams = this.$route.query;
      await this.setLeague(leagueName);
      switch (template) {
        case 'teamStats':
          if (statType === 'season') this.setType('team', 'season');
          else if (statType === 'game') this.setType('team', 'game');
          break;

        default: // playerStats
          if (statType === 'season') this.setType('player', 'season');
          else if (statType === 'game') this.setType('player', 'game');
          break;
      }
      this.isLoadingData = false;
      this.updateControlOptions({
        statType: this.statType,
        statPeriod: this.statPeriod,
      });
      return;
    }

    if (this.leagueName) {
      this.isLoadingData = true;
      try {
        await this.setLeague(this.leagueName);
        if (!this.sportradarElementId) return;

        const league = leagueInfo[this.leagueName];
        const {
          statType: leagueDefaultStatType,
          statPeriod: leagueDefaultStatPeriod,
        } = league?.statSelectorDefaults || {};

        if (leagueDefaultStatType && leagueDefaultStatPeriod) {
          this.setType(leagueDefaultStatType, leagueDefaultStatPeriod);
          this.updateControlOptions({
            statType: this.statType,
            statPeriod: this.statPeriod,
          });
        }
      } catch (error) {
        console.error('Failed to load league:', error);
        this.showNoLeagueText = true;
      } finally {
        this.isLoadingData = false;
      }
    }
  },
  methods: {
    ...mapActions('graphicsTemplate', [
      'addNewElement',
      'setElementGroupMapById',
      'setOneElementValueByProp',
      'removeElementAndMappings',
      'takeHistorySnapshot',
    ]),
    ...mapActions({
      createSportradarGraphicFromTemplate(dispatch, payload) {
        return dispatch(
          `${this.sportradarElementId}/createSportradarGraphicFromTemplate`,
          payload,
        );
      },
      createUnstyledGraphic(dispatch, payload) {
        return dispatch(
          `${this.sportradarElementId}/createUnstyledGraphic`,
          payload,
        );
      },
      setLeagueAndGetSchema(dispatch, payload) {
        return dispatch(`${this.sportradarElementId}/setLeagueAndGetSchema`, payload);
      },
      updateControlOptions(dispatch, payload) {
        return dispatch(`${this.sportradarElementId}/updateControlOptions`, payload);
      },
    }),
    updateElement(propKey, propValue, id = this.sportradarElementId) {
      this.setOneElementValueByProp({
        value: {
          id,
          propKey,
          propValue,
        },
      });
    },
    async addElement(type, options) {
      const { settings, parentId } = options;
      return this.addNewElement({
        value: {
          type,
          parentId,
          settings,
        },
      });
    },
    async addMappedElements(elSettings) {
      // TODO: change all of this once working on the custom data set/mapping
      elSettings.forEach(async (settings) => {
        const syncedLabel = !!settings.syncLabel;

        const newType = settings.type ?? 'text'; // TODO: more element types

        // eslint-disable-next-line no-param-reassign
        if (settings.syncLabel) delete settings.syncLabel;

        if (syncedLabel) {
          const linkedGroupElement = await this.addElement('linked-data', {
            parentId: this.sportradarElementId,
            settings: {
              name: 'Linked Items!',
            },
          });
          this.setElementGroupMapById({
            value: {
              id: linkedGroupElement.id,
              childIds: [],
            },
          });
          const addedElement = await this.addElement(newType, {
            settings,
            parentId: linkedGroupElement.id,
          });
          const { dataKey, provider } = settings.data;
          const { top, left, height } = addedElement.styles;

          const labelSettings = {
            type: 'text',
            parentId: linkedGroupElement.id,
            settings: {
              name: `${dataKey} label`,
              data: {
                dataKey,
                isLabel: true,
                provider,
                linkedElement: addedElement.id,
              },
              styles: {
                top: top - height,
                left,
              },
            },
          };

          const labelElement = await this.addElement(labelSettings.type, {
            parentId: labelSettings.parentId,
            settings: labelSettings.settings,
          });

          this.updateElement('data.linkedElement', labelElement.id, addedElement.id);
        } else {
          this.addElement(newType, {
            settings,
            parentId: this.sportradarElementId,
          });
        }
      });

      this.close();
    },
    async setLeague(leagueName) {
      this.showNoLeagueText = false;
      if (
        Array.isArray(this.availableSportradarLeagues)
        && !this.availableSportradarLeagues.includes(leagueName)
      ) {
        this.showNoLeagueText = true;
        return;
      }

      // add element
      const element = await this.addNewElement({
        value: {
          type: 'sportradar',
          settings: {
            data: {
              leagueName,
            },
          },
        },
        options: {
          blacklist: true,
        },
      });

      this.sportradarElementId = element.id;
      const league = leagueInfo[leagueName];
      const poweredByLabel = league?.poweredByLabel ?? 'Sportradar';
      if (league) {
        this.overline = `${league.label} • Powered by ${poweredByLabel}`;
      } else {
        this.overline = 'Powered by Sportradar';
      }

      // create "group"
      await this.setElementGroupMapById({
        value: {
          id: element.id,
          childIds: [],
        },
        options: {
          blacklist: true,
        },
      });

      // Since we are blacklisting the creation of many elements, we need to take
      // a history snapshot when done, to have an undo point to get back to.
      this.takeHistorySnapshot();

      await this.setLeagueAndGetSchema({ leagueName });
    },

    createGraphic({ stats }) {
      this.updateControlOptions({
        statType: this.statType,
        statPeriod: this.statPeriod,
      });
      if (this.templateParams) {
        this.createSportradarGraphicFromTemplate({
          provider: this.sportradarElementId,
          stats,
          templateSettings: this.templateParams,
        });
      } else {
        // no template
        this.createUnstyledGraphic({
          provider: this.sportradarElementId,
          stats,
          statType: this.statType,
        });
      }
      this.close();
    },

    setType(type, period) {
      this.transitionName = SlideTransitionName;
      this.statType = type;
      this.statPeriod = period;
      this.modalTitle = 'Choose Stats';
    },
    goBack() {
      this.transitionName = `${SlideTransitionName}--reverse`;
      this.statType = '';
      this.statPeriod = '';
      this.modalTitle = 'Select Stat Type';
    },
    onCancel() {
      if (!this.sportradarElementId) {
        this.close();
        return;
      }

      this.removeElementAndMappings({
        value: {
          element: {
            id: this.sportradarElementId,
            type: 'sportradar',
          },
        },
        options: {
          blacklist: true,
        },
      });

      if (this.isFromTemplate) {
        this.$router.replace({ query: null }); // reset template params since they closed out
        this.isFromTemplate = false;
      }

      // cleanup store module, because we aren't going to use it now
      if (this.$store.hasModule(this.sportradarElementId)) {
        this.$store.unregisterModule(this.sportradarElementId);
      }

      this.close();
    },

    close() {
      // Make sure modal isn't forcefully opened again
      this.$emit('close');
    },
  },
};
</script>
